import { Injectable } from '@angular/core';
import { ValidatorFn, AbstractControl, UntypedFormControl, } from '@angular/forms';
import { DateTime } from 'luxon';
import { EshavaFormControl, EshavaFormControlType, EshavaFormControlValidationRule } from 'eshava-controls';

@Injectable({
  providedIn: 'root'
})
export class ValidatorService {

  constructor() { }

  public equalTo(referenceControls: UntypedFormControl[]): ValidatorFn {

    return (control: AbstractControl): { [key: string]: any } | null => {
      let validTotal = true;
      const ignoreReferenceControls = (control as unknown as EshavaFormControl).ignoreReferenceControlsForValidation;
      (control as unknown as EshavaFormControl).ignoreReferenceControlsForValidation = false;

      for (const referenceControl of referenceControls) {
        const controlValue = (control.value || '').toString();
        const referenceValue = (referenceControl.value || '').toString();
        const valid = controlValue === referenceValue;

        if (!ignoreReferenceControls) {
          if (valid) {
            if (referenceControl.errors && referenceControl.errors['equalTo']) {
              delete referenceControl.errors['equalTo'];
              (referenceControl as unknown as EshavaFormControl).ignoreReferenceControlsForValidation = true;
              referenceControl.updateValueAndValidity();
            }
          } else {
            referenceControl.setErrors({ 'equalTo': { value: referenceControl.value } });
          }
        }
        validTotal = validTotal && valid;
      }

      return validTotal ? null : { 'equalTo': { value: control.value } };
    };
  }

  public notEqualTo(referenceControls: UntypedFormControl[], defaultValue: string): ValidatorFn {

    return (control: AbstractControl): { [key: string]: any } | null => {
      let validTotal = true;
      const ignoreReferenceControls = (control as unknown as EshavaFormControl).ignoreReferenceControlsForValidation;
      (control as unknown as EshavaFormControl).ignoreReferenceControlsForValidation = false;

      for (const referenceControl of referenceControls) {
        const controlValue = (control.value || '').toString();
        const referenceValue = (referenceControl.value || '').toString();
        const valid = controlValue !== referenceValue || controlValue === defaultValue;

        if (!ignoreReferenceControls) {
          if (valid) {
            if (referenceControl.errors && referenceControl.errors['notEqualTo']) {
              delete referenceControl.errors['notEqualTo'];
              (referenceControl as unknown as EshavaFormControl).ignoreReferenceControlsForValidation = true;
              referenceControl.updateValueAndValidity();
            }
          } else {
            referenceControl.setErrors({ 'notEqualTo': { value: referenceControl.value } });
          }
        }
        validTotal = validTotal && valid;
      }

      return validTotal ? null : { 'notEqualTo': { value: control.value } };
    };
  }

  public range(rule: EshavaFormControlValidationRule): ValidatorFn {

    return (control: AbstractControl): { [key: string]: any } | null => {

      if (!control.value) {
        return null;
      }

      let controlValue = 0;
      if (control.value) {
        controlValue = parseFloat(control.value.toString().replace(',', '.'));
      }
      const valid = controlValue >= (rule.minimum || 0) && controlValue <= (rule.maximum || 0);

      return valid ? null : { 'range': { value: control.value } };
    };
  }

  public rangeFrom(referenceControl: EshavaFormControl, allowNull: boolean, invert: boolean): ValidatorFn {

    return (control: AbstractControl): { [key: string]: any } | null => {
      const ignoreReferenceControls = (control as unknown as EshavaFormControl).ignoreReferenceControlsForValidation;
      (control as unknown as EshavaFormControl).ignoreReferenceControlsForValidation = false;

      const dataType = referenceControl.validationConfig.dataType;
      let valid = true;

      if ((control.value === null || control.value === undefined) &&
        (referenceControl.value === null || referenceControl.value === undefined)) {
        valid = true;

      } else if (control.value === null || control.value === undefined ||
        referenceControl.value === null || referenceControl.value === undefined) {
        valid = allowNull;

      } else if (dataType === EshavaFormControlType.dateTime || dataType === EshavaFormControlType.date || dataType === EshavaFormControlType.time) {
        const dateTimeFrom = invert ? DateTime.fromISO(control.value) : DateTime.fromISO(referenceControl.value);
        const dateTimeTo = invert ? DateTime.fromISO(referenceControl.value) : DateTime.fromISO(control.value);

        valid = dateTimeFrom <= dateTimeTo;
      } else if (dataType === EshavaFormControlType.number) {
        const numberFrom = invert ? parseFloat(control.value) : parseFloat(referenceControl.value);
        const numberTo = invert ? parseFloat(referenceControl.value) : parseFloat(control.value);

        valid = numberFrom <= numberTo;
      }

      if (valid) {
        const errorKeyReference = invert ? 'rangeTo' : 'rangeFrom';

        if (!ignoreReferenceControls && referenceControl.errors && referenceControl.errors[errorKeyReference]) {
          delete referenceControl.errors[errorKeyReference];
          (referenceControl as EshavaFormControl).ignoreReferenceControlsForValidation = true;
          referenceControl.updateValueAndValidity();
        }

        return null;
      }

      if (control.touched || control.dirty) {
        referenceControl.markAsTouched();
        referenceControl.markAsDirty();
      }

      if (invert) {
        referenceControl.setErrors({ 'rangeTo': { value: referenceControl.value } });

        return { 'rangeFrom': { value: control.value } };
      }

      referenceControl.setErrors({ 'rangeFrom': { value: referenceControl.value } });

      return { 'rangeTo': { value: control.value } };
    };
  }

  public rangeBetween(controlFrom: EshavaFormControl, controlTo: EshavaFormControl): ValidatorFn {

    return (control: AbstractControl): { [key: string]: any } | null => {

      const dataType = controlFrom.validationConfig.dataType;
      let valid = true;

      if (control.value === null || control.value === undefined ||
        controlFrom.value === null || controlFrom.value === undefined ||
        controlTo.value === null || controlTo.value === undefined) {
        valid = false;

      } else if (dataType === EshavaFormControlType.dateTime || dataType === EshavaFormControlType.date || dataType === EshavaFormControlType.time) {
        const dateTimeFrom = DateTime.fromISO(controlFrom.value);
        const dateTimeTo = DateTime.fromISO(controlTo.value);
        const dateTimeCurrent = DateTime.fromISO(control.value);

        valid = dateTimeCurrent >= dateTimeFrom && dateTimeCurrent <= dateTimeTo;

      } else if (dataType === EshavaFormControlType.number) {
        const numberFrom = parseFloat(controlFrom.value);
        const numberTo = parseFloat(controlTo.value);
        const numberCurrent = parseFloat(control.value);

        valid = numberCurrent >= numberFrom && numberCurrent <= numberTo;
      }

      return valid ? null : { 'rangeBetween': { value: control.value } };
    };
  }

  public url(): ValidatorFn {

    return (control: AbstractControl): { [key: string]: any } | null => {

      if (!control.value) {
        return null;
      }

      const valid = /^(https?|s?ftp):\/\/(((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!$&'()*+,;=]|:)*@)?((localhost)|((\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5]))|((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.?)(:\d*)?)(\/((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!$&'()*+,;=]|:|@)+(\/(([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!$&'()*+,;=]|:|@)*)*)?)?(\?((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!$&'()*+,;=]|:|@)|[\uE000-\uF8FF]|\/|\?)*)?(#((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!$&'()*+,;=]|:|@)|\/|\?)*)?$/i.test(control.value);

      return valid ? null : { 'url': { value: control.value } };
    };
  }

  public guid(): ValidatorFn {

    return (control: AbstractControl): { [key: string]: any } | null => {

      if (!control.value) {
        return null;
      }

      const regExGuid = new RegExp('^[A-Fa-f0-9]{8}-([A-Fa-f0-9]{4}-){3}[A-Fa-f0-9]{12}$');
      const regExGuidEmpty = new RegExp('^[0]{8}-([0]{4}-){3}[0]{12}$');
      const valid = regExGuid.test(control.value) && !regExGuidEmpty.test(control.value);

      return valid ? null : { 'guid': { value: control.value } };
    };
  }

  public phoneNumber(): ValidatorFn {

    return (control: AbstractControl): { [key: string]: any } | null => {

      if (!control.value) {
        return null;
      }

      const regExPhone = new RegExp('^[+]?[0-9]{3,6}[ ]?[0-9]{5,8}$');
      const valid = regExPhone.test(control.value);

      return valid ? null : { 'phone': { value: control.value } };
    };
  }

  public mobileNumber(): ValidatorFn {

    return (control: AbstractControl): { [key: string]: any } | null => {

      if (!control.value) {
        return null;
      }

      const regExMobile = new RegExp('^[+]?[0-9]{3,6}[ ]?[0-9]{5,8}$');
      const valid = regExMobile.test(control.value);

      return valid ? null : { 'mobile': { value: control.value } };
    };
  }

  public decimals(decimalPlaces: number): ValidatorFn {

    return (control: AbstractControl): { [key: string]: any } | null => {

      if (!control.value) {
        return null;
      }

      let valid = true;

      if (decimalPlaces <= 0 && !/^\d+$/i.test(control.value)) {
        valid = false;

      } else if (decimalPlaces > 0) {
        const regEx = new RegExp('^-?(?:\\d+|\\d{1,3}(?:,\\d{3})+)(?:[\\,\\.][\\d+]{0,' + decimalPlaces + '})?$', 'i');
        valid = regEx.test(control.value);

      }

      return valid ? null : { 'decimalplaces': { value: control.value, decimals: decimalPlaces } };
    };
  }

  public number(): ValidatorFn {

    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!control.value) {
        return null;
      }

      const valid = !isNaN(Number(control.value.toString().replace(',', '.')));

      return valid ? null : { 'nan': { value: control.value } };
    };
  }

  public setError(control: EshavaFormControl, errorKey: string, valid: boolean): void {
    control.markAsTouched();

    if (valid) {
      if (control.errors && control.errors[errorKey]) {
        delete control.errors[errorKey];
        control.updateValueAndValidity();
      }
    } else {
      const error: any = {};
      error[errorKey] = { value: control.value };
      control.setErrors(error);
    }
  }
}
